import type { ConfigurationsScope, QueryScope } from 'stores/mobx/configuration-properties.store';
import { ConfigurationPropertiesStore } from 'stores/mobx/configuration-properties.store';
import { action, computed, observable } from 'mobx';
import some from 'lodash/some';
import { ConfigurationScope } from '@env0/configuration-service/api.enum';
import type { EnvironmentApi } from '@env0/environment-service/api';
import { ConfigurationPropertyStore } from 'stores/mobx/configuration-property.store';
import type { ConfigurationProperty, VariablesPolicies } from 'types/api.types';
import pickBy from 'lodash/pickBy';
import map from 'lodash/map';
import type ServiceContainer from 'services/service-container';
import { HierarchicalVariablesStore } from 'stores/mobx/hierarchical-variables-store';

export const entireWorkflowAlias = 'entireWorkflow';

export class WorkflowVariablesStore extends HierarchicalVariablesStore {
  @observable
  private workflowConfigurationPropertiesStores: Record<string, ConfigurationPropertiesStore> = {};
  private entireWorkflowConfigurationPropertiesStore: ConfigurationPropertiesStore;

  constructor(service: ServiceContainer) {
    super(service);
    this.entireWorkflowConfigurationPropertiesStore = new ConfigurationPropertiesStore(this.service);
  }

  getExpressionsForReactions() {
    return [
      () =>
        this.entireWorkflowConfigurationPropertiesStore.environmentVariables.filter(
          variable => variable.scope === ConfigurationScope.WORKFLOW || variable.scope === ConfigurationScope.BLUEPRINT
        ) || []
    ];
  }

  public init(subEnvironments: Record<string, EnvironmentApi.WorkflowEnvironments.WorkflowSubEnvironment>) {
    this.workflowConfigurationPropertiesStores = Object.fromEntries(
      Object.keys(subEnvironments).map(alias => [alias, new ConfigurationPropertiesStore(this.service)])
    );
  }

  private async loadWorkflowVariables(
    subEnvironments: Record<string, EnvironmentApi.WorkflowEnvironments.WorkflowSubEnvironment>,
    getQueryScope: (
      alias: string,
      subEnvironment: EnvironmentApi.WorkflowEnvironments.WorkflowSubEnvironment
    ) => QueryScope & ConfigurationsScope,
    variablesPolicies?: Partial<VariablesPolicies>
  ) {
    await Promise.allSettled(
      Object.keys(subEnvironments).map(alias => {
        const subEnvironment = subEnvironments[alias];
        const subEnvironmentConfigurationStore = this.getConfigurationPropertiesStoreByAlias(alias);
        subEnvironmentConfigurationStore.resetConfigurationSetChanges();
        return subEnvironmentConfigurationStore.loadProperties(
          getQueryScope(alias, subEnvironment),
          true,
          undefined,
          variablesPolicies
        );
      })
    );
  }

  public static getWorkflowBlueprintIdSubAlias = (alias: string, templateId?: string) => {
    return alias !== entireWorkflowAlias && templateId ? `${templateId}:${alias}` : undefined;
  };

  public static getSubEnvironmentBlueprintScopeId = (alias: string, templateId?: string) => {
    if (alias === entireWorkflowAlias) {
      return templateId ? templateId : entireWorkflowAlias;
    }
    return templateId ? `${templateId}:${alias}` : alias;
  };

  public async loadWorkflowEnvironmentVariables(
    subEnvironments: Record<string, EnvironmentApi.WorkflowEnvironments.WorkflowSubEnvironment>,
    projectId: string | undefined,
    workflowEnvironmentId: string | undefined,
    variablesPolicies?: Partial<VariablesPolicies>
  ) {
    const subEnvironmentAndWorkflowQueryScope = (
      alias: string,
      subEnvironment: EnvironmentApi.WorkflowEnvironments.WorkflowSubEnvironment
    ): QueryScope & ConfigurationsScope => ({
      projectId,
      blueprintId: subEnvironment.templateId,
      workflowBlueprintIdSubAlias: WorkflowVariablesStore.getWorkflowBlueprintIdSubAlias(
        alias,
        subEnvironments[entireWorkflowAlias]?.templateId
      ),
      workflowEnvironmentId: workflowEnvironmentId,
      scope: alias !== entireWorkflowAlias ? ConfigurationScope.ENVIRONMENT : ConfigurationScope.WORKFLOW,
      scopeId: subEnvironment.environmentId,
      environmentId: subEnvironment.environmentId
    });
    await this.loadWorkflowVariables(subEnvironments, subEnvironmentAndWorkflowQueryScope, variablesPolicies);
  }

  public async loadWorkflowTemplateVariables(
    subEnvironments: Record<string, EnvironmentApi.WorkflowEnvironments.WorkflowSubEnvironment>,
    projectId: string | undefined,
    variablesPolicies?: Partial<VariablesPolicies>
  ) {
    const workflowBlueprintId = subEnvironments[entireWorkflowAlias]?.templateId;
    const subEnvironmentTemplateAndWorkflowQueryScope = (
      alias: string,
      subEnvironment: EnvironmentApi.WorkflowEnvironments.WorkflowSubEnvironment
    ): QueryScope & ConfigurationsScope => ({
      blueprintId: subEnvironment.templateId,
      projectId,
      workflowBlueprintId,
      workflowBlueprintIdSubAlias: WorkflowVariablesStore.getWorkflowBlueprintIdSubAlias(alias, workflowBlueprintId),
      scope:
        alias !== entireWorkflowAlias ? ConfigurationScope.SUB_ENVIRONMENT_BLUEPRINT : ConfigurationScope.BLUEPRINT,
      scopeId: WorkflowVariablesStore.getSubEnvironmentBlueprintScopeId(alias, workflowBlueprintId)
    });
    await this.loadWorkflowVariables(subEnvironments, subEnvironmentTemplateAndWorkflowQueryScope, variablesPolicies);
  }

  // We call this in the hook on setup in order to propagate the workflow bp variables to the sub environments
  // since unlike project&org variables they don't get propagated from the API
  public propagateWorkflowBlueprintVariablesToSubEnvironments() {
    this.propagateVariableChanges(
      this.getConfigurationPropertiesStoreByAlias(entireWorkflowAlias).environmentVariables.filter(
        property => property.scope === ConfigurationScope.BLUEPRINT
      )
    );
  }

  public getConfigurationPropertiesStores() {
    return this.workflowConfigurationPropertiesStores;
  }

  public getConfigurationPropertiesStoreByAlias(environment: string) {
    return environment === entireWorkflowAlias
      ? this.entireWorkflowConfigurationPropertiesStore
      : this.workflowConfigurationPropertiesStores[environment];
  }

  public getConfigurationChanges(environment: string) {
    return this.getConfigurationPropertiesStoreByAlias(environment).getConfigurationPropertiesChangesForDeployment();
  }

  @computed get environmentErrors() {
    return map(
      pickBy(this.workflowConfigurationPropertiesStores, store => store.hasError),
      (_, alias) => alias
    );
  }

  @computed get hasError() {
    return (
      this.entireWorkflowConfigurationPropertiesStore.hasError ||
      some(this.workflowConfigurationPropertiesStores, store => store.hasError)
    );
  }

  protected isVariableOverridden(
    changedConfigurationProperty: ConfigurationPropertyStore,
    existingProperty: ConfigurationPropertyStore
  ): boolean {
    return existingProperty.scope === ConfigurationScope.ENVIRONMENT;
  }

  protected onPropagateChangesOfOverriddenVariable(): void {}

  buildPropagatedCreatedVariable(
    childStore: ConfigurationPropertiesStore,
    changedConfigurationProperty: ConfigurationPropertyStore
  ) {
    // TODO: this is a workaround since we rely on the FE workflow vars propagation, after we fix that we can remove this
    const isWorkflowEnvironmentVariable =
      changedConfigurationProperty.scope === ConfigurationScope.BLUEPRINT &&
      changedConfigurationProperty.editScope === ConfigurationScope.WORKFLOW;
    const newData: ConfigurationProperty = {
      ...changedConfigurationProperty.data,
      scope: isWorkflowEnvironmentVariable ? ConfigurationScope.WORKFLOW : changedConfigurationProperty.scope,
      id: changedConfigurationProperty.id
    };
    return new ConfigurationPropertyStore(childStore, newData);
  }

  buildPropagatedUpdatedVariable(changedConfigurationProperty: ConfigurationPropertyStore) {
    return changedConfigurationProperty.data;
  }

  getRelevantStoresForPropagation(): ConfigurationPropertiesStore[] {
    return Object.values(this.workflowConfigurationPropertiesStores);
  }

  @computed get workflowVariablesIsDirty() {
    const isStoreVariablesDirty = (store: ConfigurationPropertiesStore) =>
      store.environmentVariablesIsDirty || store.terraformVariablesIsDirty;

    return (
      isStoreVariablesDirty(this.entireWorkflowConfigurationPropertiesStore) ||
      some(this.workflowConfigurationPropertiesStores, store => isStoreVariablesDirty(store))
    );
  }

  @action
  public cleanup() {
    this.workflowConfigurationPropertiesStores = {};
    this.disposeChanges?.();
  }
}
